import React, { useEffect, useState, useContext, useRef } from "react";
import { Box, Typography, Chip, Card, CardContent, Paper } from "@mui/material";
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import { applySalesMultiplier } from '../utils/chartUtils';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RecommendIcon from '@mui/icons-material/Recommend';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { createRoot } from 'react-dom/client';
import SubjectActionItem from './dashboard/SubjectActionItem';
import CategoryItem from './dashboard/CategoryItem';
import QuickOverview from './dashboard/QuickOverview';
import RecommendedActions from './dashboard/RecommendedActions';

const DashboardOverview = () => {
  const { selectedBusiness, currencySymbol } = useContext(BusinessContext);
  const { 
    reviewsData, 
    view, 
    timeframe, 
    sortOption, 
    selectedSubject,
    setSelectedSubject,
    selectedAttribute,
    setSelectedAttribute,
    selectionSource,
    setSelectionSource,
    selectedCategory,
    setSelectedCategory,
    selectedDate,
    setSelectedDate
  } = useContext(ReviewsContext);
  
  const [overallStats, setOverallStats] = useState({
    positiveImpact: 0,
    negativeImpact: 0,
    topSubjects: []
  });
  
  const [topSubjects, setTopSubjects] = useState([]);
  const [subjectAttributes, setSubjectAttributes] = useState({});
  const [categoryStats, setCategoryStats] = useState({
    topCategories: [],
    totalPositive: 0,
    totalNegative: 0
  });
  
  // Reference to the dashboard overview container
  const dashboardRef = useRef(null);
  
  // Add document click listener to reset filters when clicking elsewhere
  useEffect(() => {
    // Function to handle clicks outside the filter terms
    const handleOutsideClick = (event) => {
      // Check if we have any filter selected
      if (selectedSubject || selectedAttribute || selectedCategory) {
        console.log('Click event in DashboardOverview:', event.target);
        
        // Don't clear filters if the click was on a filter term or on a chart element
        const isFilterTerm = event.target.dataset.filterTerm;
        
        // Check if the click was on a category box
        const isCategoryBox = 
          event.target.dataset.categoryBox === 'true' || 
          !!event.target.closest('[data-category-box="true"]') || 
          !!event.target.closest('[data-filter-term="category"]');
        
        // More comprehensive check for chart elements
        const isChartElement = 
          // Check for standard chart elements
          event.target.tagName === 'rect' || 
          event.target.tagName === 'path' ||
          event.target.closest('.MuiChartsLegend-root') || 
          event.target.closest('.MuiChartsAxis-root') ||
          // Check for any element within a chart
          !!event.target.closest('.MuiChart-root') ||
          !!event.target.closest('svg') ||
          // Check for specific bar chart elements
          event.target.classList?.contains('MuiBarElement-root') ||
          !!event.target.closest('.MuiBarElement-root') ||
          !!event.target.closest('[role="graphics-document"]');
        
        // Also check if the click was on an element that should be exempt from filter clearing
        const isExemptFromFiltering = 
          event.target.dataset.filterExempt === 'true' ||
          !!event.target.closest('[data-filter-exempt="true"]') ||
          // Check for our custom chart markers
          !!event.target.closest('[data-chart-type="attribute-chart"]') ||
          !!event.target.closest('[data-chart-type="subject-chart"]') ||
          // Consider the entire chart cards exempt
          !!event.target.closest('.MuiChartsBar-root') ||
          !!event.target.closest('.MuiBarChart-root');
        
        if (!isFilterTerm && !isChartElement && !isExemptFromFiltering && !isCategoryBox) {
          // Clear all filters when clicking anywhere except on a filter term, chart element, or exempt element
          setSelectedSubject(null);
          setSelectedAttribute(null);
          setSelectedCategory && setSelectedCategory(null);
        }
      }
    };
    
    // Add the event listener
    document.addEventListener('mousedown', handleOutsideClick);
    
    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [selectedSubject, selectedAttribute, selectedCategory, setSelectedSubject, setSelectedAttribute, setSelectedCategory]);
  
  useEffect(() => {
    try {
      // If no data, reset all stats to defaults/empty values
      if (!reviewsData || !reviewsData.length) {
        setOverallStats({
          positiveImpact: 0,
          negativeImpact: 0,
          positiveRevenueImpact: 0,
          negativeRevenueImpact: 0,
          topSubjects: []
        });
        
        setTopSubjects([]);
        setSubjectAttributes({});
        
        // Reset category stats with empty values but maintain the structure
        const emptyCategories = ['product', 'service', 'experience'].map(category => ({
          name: category,
          positive: 0,
          negative: 0, 
          total: 0,
          impact: 0,
          isPositive: true
        }));
        
        setCategoryStats({
          topCategories: emptyCategories,
          totalPositive: 0,
          totalNegative: 0
        });
        
        return;
      }
      
      // Calculate impact stats
      let positiveImpact = 0;
      let negativeImpact = 0;
      const subjectImpact = {};
      const attributesBySubject = {};
      const categoryImpact = {};
      
      reviewsData.forEach(review => {
        if (!review.analysis || review.analysis.length === 0) return;
        
        review.analysis.forEach(item => {
          const { subject_group, attribute, attribute_impact, sentiment, category } = item;
          
          // Overall impact
          if (attribute_impact > 0) {
            positiveImpact += attribute_impact;
          } else if (attribute_impact < 0) {
            negativeImpact += attribute_impact;
          }
          
          // Subject impact
          if (subject_group) {
            if (!subjectImpact[subject_group]) {
              subjectImpact[subject_group] = { positive: 0, negative: 0, total: 0 };
            }
            
            if (attribute_impact > 0) {
              subjectImpact[subject_group].positive += attribute_impact;
            } else if (attribute_impact < 0) {
              subjectImpact[subject_group].negative += attribute_impact;
            }
            
            subjectImpact[subject_group].total += attribute_impact;
            
            // Track attributes for each subject
            if (attribute) {
              if (!attributesBySubject[subject_group]) {
                attributesBySubject[subject_group] = {
                  positive: {},
                  negative: {}
                };
              }
              
              // Use sentiment from the review data to determine the bucket
              const sentimentBucket = sentiment === 'positive' ? 'positive' : 'negative';
              
              // Initialize this attribute's impact if not already there
              if (!attributesBySubject[subject_group][sentimentBucket][attribute]) {
                attributesBySubject[subject_group][sentimentBucket][attribute] = 0;
              }
              
              // Accumulate impact with the actual value
              attributesBySubject[subject_group][sentimentBucket][attribute] += Math.abs(attribute_impact);
            }
          }
          
          // Category impact
          if (category) {
            if (!categoryImpact[category]) {
              categoryImpact[category] = { positive: 0, negative: 0, total: 0 };
            }
            
            if (attribute_impact > 0) {
              categoryImpact[category].positive += attribute_impact;
            } else if (attribute_impact < 0) {
              categoryImpact[category].negative += attribute_impact;
            }
            
            categoryImpact[category].total += attribute_impact;
          }
        });
      });
      
      // Create separate entries for positive and negative impacts
      let topImpactSubjectsArray = [];
      
      // For 'total' view, we should consider both positive and negative impacts for each subject
      if (sortOption === 'total') {
        // Add entries for positive impacts
        Object.keys(subjectImpact).forEach(subject => {
          if (subjectImpact[subject].positive > 0) {
            topImpactSubjectsArray.push({
              name: subject,
              impact: subjectImpact[subject].positive,
              isPositive: true,
              revenueImpact: 0
            });
          }
          
          // Add separate entries for negative impacts
          if (subjectImpact[subject].negative < 0) {
            topImpactSubjectsArray.push({
              name: subject,
              impact: Math.abs(subjectImpact[subject].negative),
              isPositive: false,
              revenueImpact: 0
            });
          }
        });
        
        // Sort by impact magnitude and take top 3
        topImpactSubjectsArray.sort((a, b) => b.impact - a.impact);
        topImpactSubjectsArray = topImpactSubjectsArray.slice(0, 3);
      } else {
        // Sort top impact subjects for displaying in the Quick Overview section - for positive/negative filters
        let topImpactSubjects = Object.keys(subjectImpact).map(subject => ({
          name: subject,
          positive: subjectImpact[subject].positive,
          negative: subjectImpact[subject].negative,
          total: subjectImpact[subject].total,
          impact: sortOption === 'positive' 
            ? subjectImpact[subject].positive 
            : Math.abs(subjectImpact[subject].negative),
          isPositive: sortOption === 'positive',
          // Initialize revenueImpact to prevent undefined
          revenueImpact: 0
        }));
        
        // Filter impacts based on sort option
        if (sortOption === 'positive') {
          // Only include subjects with positive impact
          topImpactSubjects = topImpactSubjects.filter(subject => subject.positive > 0);
        } else if (sortOption === 'negative') {
          // Only include subjects with negative impact
          topImpactSubjects = topImpactSubjects.filter(subject => subject.negative < 0);
        }
        
        // Sort top subjects
        topImpactSubjects.sort((a, b) => b.impact - a.impact);
        topImpactSubjects = topImpactSubjects.slice(0, 3);
        
        topImpactSubjectsArray = topImpactSubjects;
      }
      
      // Save top subjects for recommendations
      setTopSubjects(topImpactSubjectsArray);
      
      // Process attributes for each top subject
      const topAttributesBySubject = {};
      
      // Process each subject entry (not just unique names)
      // This ensures that attributes are properly matched to their specific subject instances
      topImpactSubjectsArray.forEach(subject => {
        const subjectName = subject.name;
        const sentiment = subject.isPositive ? 'positive' : 'negative';
        
        // Create a unique key for this subject instance to avoid mixing positive and negative attributes
        const subjectKey = `${subjectName}:${sentiment}`;
        
        // Initialize the attribute array for this subject key
        if (!topAttributesBySubject[subjectKey]) {
          topAttributesBySubject[subjectKey] = [];
        }
        
        // Process the attributes for this subject and sentiment
        if (attributesBySubject[subjectName] && attributesBySubject[subjectName][sentiment]) {
          // Get attributes matching the subject sentiment
          const attributes = attributesBySubject[subjectName][sentiment];
          
          // Convert to array and sort by impact
          const sortedAttributes = Object.entries(attributes)
            .map(([name, impact]) => ({ 
              name, 
              impact,
              sentiment, // Add the sentiment to track which sentiment this attribute belongs to
              // Add revenue impact calculation for each attribute
              revenueImpact: view === "Sales" && selectedBusiness?.turnover 
                ? impact * selectedBusiness.turnover * 0.1 
                : 0,
              // We're not storing category per attribute currently since data structure doesn't support it
              // For filtering by category, we'd need to enhance the data model
              category: null
            }))
            // Sort by actual impact values (high to low)
            .sort((a, b) => b.impact - a.impact)
            .slice(0, 3); // Take top 3
          
          // Set the attributes for this subject key
          topAttributesBySubject[subjectKey] = sortedAttributes;
        }
      });
      
      setSubjectAttributes(topAttributesBySubject);
      
      // Calculate revenue impacts
      let positiveRevenueImpact = positiveImpact;
      let negativeRevenueImpact = negativeImpact;
      
      if (view === "Sales" && selectedBusiness?.turnover) {
        const multiplier = selectedBusiness.turnover * 0.1; // Same as SALES_MULTIPLIER
        positiveRevenueImpact = positiveImpact * multiplier;
        negativeRevenueImpact = negativeImpact * multiplier;
        
        // Apply revenue calculations to top subjects
        topImpactSubjectsArray = topImpactSubjectsArray.map(subject => ({
          ...subject,
          revenueImpact: subject.impact * multiplier
        }));
      }
      
      setOverallStats({
        positiveImpact,
        negativeImpact,
        positiveRevenueImpact,
        negativeRevenueImpact,
        topSubjects: topImpactSubjectsArray
      });
      
      // Process categories
      let totalCategoryPositive = 0;
      let totalCategoryNegative = 0;
      
      // Define our three fixed categories
      const mainCategories = ['product', 'service', 'experience'];
      
      // Ensure all three main categories are included
      mainCategories.forEach(category => {
        if (!categoryImpact[category]) {
          categoryImpact[category] = { positive: 0, negative: 0, total: 0 };
        }
      });
      
      // Create an array with just our three main categories
      const categoryArray = mainCategories.map(category => {
        totalCategoryPositive += categoryImpact[category]?.positive || 0;
        totalCategoryNegative += categoryImpact[category]?.negative || 0;
        
        return {
          name: category,
          positive: categoryImpact[category]?.positive || 0,
          negative: categoryImpact[category]?.negative || 0,
          total: Math.abs(categoryImpact[category]?.positive || 0) + Math.abs(categoryImpact[category]?.negative || 0),
          impact: sortOption === 'positive' 
            ? categoryImpact[category]?.positive || 0 
            : sortOption === 'negative'
              ? Math.abs(categoryImpact[category]?.negative || 0)
              : Math.abs(categoryImpact[category]?.positive || 0) + Math.abs(categoryImpact[category]?.negative || 0),
          isPositive: sortOption !== 'negative' // For display purposes
        };
      });
      
      // Sort categories by impact
      const sortedCategories = [...categoryArray].sort((a, b) => b.impact - a.impact);
      
      // Apply revenue impact calculations if needed
      if (view === "Sales" && selectedBusiness?.turnover) {
        const multiplier = selectedBusiness.turnover * 0.1;
        totalCategoryPositive *= multiplier;
        totalCategoryNegative *= multiplier;
        
        // Update categories with revenue impact
        sortedCategories.forEach(category => {
          category.revenueImpact = category.impact * multiplier;
        });
      }
      
      // Store category stats - use sortedCategories as topCategories
      setCategoryStats({
        topCategories: sortedCategories,
        totalPositive: totalCategoryPositive,
        totalNegative: totalCategoryNegative
      });
      
    } catch (error) {
      console.error("Error calculating overview stats:", error);
    }
  }, [reviewsData, view, sortOption, selectedBusiness]);

  // Add a useEffect to monitor and preserve selections when view changes
  useEffect(() => {
    // When view changes, ensure that the current selections are preserved
    if (selectedCategory) {
      // This is already in the global state, so nothing to do
    }
  }, [view, selectedCategory]);
  
  // Format numbers for display
  const formatNumber = (num, isCurrency = false) => {
    if (num === undefined || num === null || Math.abs(num) < 0.001) return isCurrency ? `${currencySymbol}0` : '0★';
    
    const formatted = Math.abs(num).toFixed(3);
    return isCurrency 
      ? `${currencySymbol}${formatted}`
      : `${formatted}★`;
  };
  
  // Format impact values with dynamic precision to ensure significant digits are shown
  const formatImpact = (impact) => {
    if (impact === undefined || impact === null) return '0.000 ★';
    
    const absImpact = Math.abs(impact);
    
    // For zero values
    if (absImpact === 0) return '0.000 ★';
    
    // For normal values (≥ 0.001), use standard 3 decimal places
    if (absImpact >= 0.001) {
      return `${absImpact.toFixed(3)} ★`;
    }
    
    // For very small values, find the first significant digit using scientific notation
    // This is much more reliable for extremely small numbers
    const scientificNotation = absImpact.toExponential();
    
    // Parse the scientific notation (e.g., "3.45e-6")
    const matches = scientificNotation.match(/^(\d)\.?(\d*)?e-(\d+)$/);
    
    if (matches) {
      const firstDigit = matches[1]; // First significant digit
      const exponent = parseInt(matches[3], 10);
      
      // Create the string with leading zeros
      let result = '0.';
      for (let i = 1; i < exponent; i++) {
        result += '0';
      }
      result += firstDigit;
      
      return `${result} ★`;
    }
    
    // Fallback to standard formatting (should rarely happen)
    return `${absImpact.toFixed(3)} ★`;
  };
  
  // Format large currency values with k/m suffix
  const formatCurrency = (value) => {
    // Add safety check for undefined or null values
    if (value === undefined || value === null) {
      return `${currencySymbol}0`;
    }
    
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    let formattedValue = '';
    
    if (absValue >= 1000000) {
      formattedValue = `${currencySymbol}${(absValue / 1000000).toFixed(1)}m`;
    } else if (absValue >= 1000) {
      formattedValue = `${currencySymbol}${(absValue / 1000).toFixed(1)}k`;
    } else {
      formattedValue = `${currencySymbol}${absValue.toFixed(0)}`;
    }
    
    // Use regular minus sign but maintain the formatting with whiteSpace: nowrap
    return isNegative ? `-${formattedValue}` : formattedValue;
  };
  
  // Get timeframe in days as a number
  const getTimeframeDays = () => {
    if (timeframe === 'all') return 'all time';
    return `last ${timeframe} days`;
  };
  
  // Handle click on a subject term in the recommended actions
  const handleSubjectClick = (subject) => {
    const capitalizedSubject = capitalizeSubject(subject);
    
    // Check if this subject is already selected
    if (selectedSubject === capitalizedSubject) {
      // If already selected, unselect it
      setSelectedSubject(null);
      setSelectedAttribute(null);
      // Don't reset selectedCategory here
      return;
    }
    
    // Otherwise, select this subject
    setSelectedSubject(capitalizedSubject);
    setSelectedAttribute(null);
    // Don't reset selectedCategory here
  };

  // Function to determine if a subject should be highlighted or underlined
  const shouldHighlightSubject = (subjectName) => {
    const capitalizedName = capitalizeSubject(subjectName);
    
    // Simply check if this subject matches the selected subject
    if (capitalizedName === selectedSubject) {
      console.log(`[RecommendedActions] Highlighting ${capitalizedName}`);
      return true;
    }
    
    return false;
  };

  // Capitalize subject name
  const capitalizeSubject = (name) => {
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
  };
  
  // Capitalize attribute name
  const capitalizeAttribute = (name) => {
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
  };

  // Handle click on an attribute term in the recommended actions
  const handleAttributeClick = (attribute, parentSubject) => {
    const capitalizedSubject = capitalizeSubject(parentSubject);
    const capitalizedAttribute = capitalizeAttribute(attribute);
    
    // Check if this attribute is already selected
    if (selectedAttribute === capitalizedAttribute && selectedSubject === capitalizedSubject) {
      // If already selected, unselect it
      setSelectedAttribute(null);
      // Don't reset selectedCategory here
      return;
    }
    
    // Otherwise, select this attribute and its parent subject
    setSelectedSubject(capitalizedSubject);
    setSelectedAttribute(capitalizedAttribute);
    // Don't reset selectedCategory here
  };

  // In the Recommended Actions section, ensure the text is the same for both views
  const getSubjectImpactAction = (isPositive) => {
    return isPositive
      ? "Continue focus on "
      : "Improve ";
  };

  // Remove additional context so it's the same for both views
  const getSubjectImpactContext = (isPositive) => {
    return ""; // No additional context needed for either view
  };
  
  // Function to determine if an attribute should be highlighted or underlined
  const shouldHighlightAttribute = (attributeName, parentSubjectName) => {
    const capitalizedSubject = capitalizeSubject(parentSubjectName);
    const capitalizedAttribute = capitalizeAttribute(attributeName);
    
    // Simply check if this attribute and its parent subject match the selected ones
    if (capitalizedAttribute === selectedAttribute && capitalizedSubject === selectedSubject) {
      return true;
    }
    
    return false;
  };

  // Update the existing highlighting functions to use the new ones
  const isSubjectHighlighted = shouldHighlightSubject;
  const isAttributeHighlightingParent = (subjectName, attributeName) => 
    shouldHighlightAttribute(attributeName, subjectName);

  // Handle click on a category
  const handleCategoryClick = (categoryName) => {
    // Format the category name consistently
    const formattedCategory = categoryName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    // Check if this category is already selected
    if (selectedCategory === formattedCategory) {
      // If already selected, unselect it
      setSelectedCategory(null);
      return;
    }
    
    // Otherwise, select this category and clear subject/attribute selections
    setSelectedCategory(formattedCategory);
    setSelectedSubject(null);
    setSelectedAttribute(null);
  };

  // Function to check if a category is highlighted/selected
  const isCategoryHighlighted = (categoryName) => {
    const formattedCategory = categoryName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    // Simpler check - just whether this category is selected or clicked
    if (selectedCategory === formattedCategory) {
      return true;
    }
    
    return false;
  };

  // DOM manipulation to split content between sections
  useEffect(() => {
    // After component mounts and data is processed, distribute content to respective containers
    const quickOverviewContainer = document.getElementById('quick-overview-content');
    const recommendedActionsContainer = document.getElementById('recommended-actions-content');

    // Only proceed if the containers exist
    if (quickOverviewContainer && recommendedActionsContainer) {
      try {
        // Create Quick Overview content using our new component
        const quickOverviewContent = (
          <QuickOverview
            timeframe={timeframe}
            sortOption={sortOption}
            view={view}
            overallStats={overallStats}
            categoryStats={categoryStats}
            getTimeframeDays={getTimeframeDays}
            formatCurrency={formatCurrency}
            formatImpact={formatImpact}
            isCategoryHighlighted={isCategoryHighlighted}
            handleCategoryClick={handleCategoryClick}
            currencySymbol={currencySymbol}
            selectedBusiness={selectedBusiness}
          />
        );

        // Create Recommended Actions content using our new component
        const recommendedActionsContent = (
          <RecommendedActions
            overallStats={overallStats}
            topSubjects={topSubjects}
            subjectAttributes={subjectAttributes}
            view={view}
            isSubjectHighlighted={isSubjectHighlighted}
            isAttributeHighlightingParent={isAttributeHighlightingParent}
            handleSubjectClick={handleSubjectClick}
            handleAttributeClick={handleAttributeClick}
            formatCurrency={formatCurrency}
            formatImpact={formatImpact}
            capitalizeSubject={capitalizeSubject}
            capitalizeAttribute={capitalizeAttribute}
            getSubjectImpactAction={getSubjectImpactAction}
            selectedCategory={selectedCategory}
            reviewsData={reviewsData}
            sortOption={sortOption}
          />
        );

        // When re-rendering, preserve the selection state by grabbing it from the React context
        // Create roots if they don't exist
        if (!quickOverviewContainer._reactRoot) {
          quickOverviewContainer._reactRoot = createRoot(quickOverviewContainer);
        }
        if (!recommendedActionsContainer._reactRoot) {
          recommendedActionsContainer._reactRoot = createRoot(recommendedActionsContainer);
        }
        
        // Render content to their respective containers
        quickOverviewContainer._reactRoot.render(quickOverviewContent);
        recommendedActionsContainer._reactRoot.render(recommendedActionsContent);
      } catch (err) {
        console.error('Error rendering dashboard content:', err);
      }
    }
  }, [
    overallStats, 
    view, 
    timeframe, 
    sortOption, 
    topSubjects, 
    subjectAttributes, 
    selectedSubject,
    selectedAttribute,
    categoryStats,
    selectedBusiness,
    selectedCategory,
    reviewsData
  ]);

  // Return an empty fragment since actual content is rendered via refs
  return null;
};

export default DashboardOverview; 